"use client";

import type { JSX } from "react";
import { motion, type Variants } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const resources = [
  {
    date: "May 20, 2025",
    title: "Payment Trends Explained",
    description:
      "Stay updated with the latest innovations shaping the future of digital payments.",
    imageClass: "[background:url(/home-v3/resource-1.jpg)_50%_50%_/_cover]",
    imageHeight: "h-[216px]",
    dateFont: "[font-family:'Suisse_Intl_Trial-Regular',Helvetica] font-normal",
  },
  {
    date: "May 20, 2025",
    title: "Secure Transaction Guide",
    description:
      "Learn essential practices to keep every payment safe and trustworthy.",
    imageClass: "[background:url(/home-v3/resource-2.jpg)_50%_50%_/_cover]",
    imageHeight: "h-[346px]",
    dateFont: "font-inter-tight font-medium",
  },
  {
    date: "May 20, 2025",
    title: "Business Growth Insights",
    description:
      "Explore strategies and tools that help companies scale with smarter payment.",
    imageClass: "[background:url(/home-v3/resource-3.jpg)_50%_50%_/_cover]",
    imageHeight: "h-[241px]",
    dateFont: "[font-family:'Suisse_Intl_Trial-Regular',Helvetica] font-normal",
  },
  {
    date: "May 20, 2025",
    title: "Digital Commerce Evolution",
    description:
      "Discover how technology is transforming the way businesses buy and sell online.",
    imageClass: "[background:url(/home-v3/resource-4.jpg)_50%_50%_/_cover]",
    imageHeight: "h-[293px]",
    dateFont: "[font-family:'Suisse_Intl_Trial-Regular',Helvetica] font-normal",
  },
];

export const ClientOnboardingProcessSection = (): JSX.Element => {
  return (
    <section
      aria-labelledby="business-resources-heading "
      className="max-sm:mt-20 max-w-[1440px] mx-auto flex w-full flex-col items-start gap-[60px] px-5 sm:mt-40 sm:px-10 lg:mt-32 lg:px-10"
    >
      <header className="flex w-full items-end justify-center">
        <div className="flex flex-col items-center gap-4">
          <Badge className="animate-revolving-border rounded-[30px] border-0 bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] text-white backdrop-blur-[3.5px] backdrop-brightness-[100%] [-webkit-backdrop-filter:blur(3.5px)_brightness(100%)]">
            Business Resources
          </Badge>
          <motion.h2
            id="business-resources-heading"
            className="[font-family:'Suisse_Works_Trial-Regular',Helvetica] text-center text-3xl leading-[1.2] sm:text-4xl lg:text-5xl lg:leading-[60px] font-normal text-white"
            variants={headingVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Helpful guides for keeping
            <br />
            your company organized
          </motion.h2>
        </div>
      </header>
      <div className="grid w-full grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {resources.map((resource) => (
          <article key={resource.title} className="min-w-0">
            <Card className="h-full overflow-hidden rounded-none border-0 bg-transparent shadow-none">
              <CardContent className="flex h-full flex-col p-0">
                <div
                  aria-hidden="true"
                  className={`w-full rounded-3xl ${resource.imageHeight} ${resource.imageClass}`}
                />
                <div className="flex flex-1 flex-col items-start pt-5">
                  <time
                    dateTime="2025-05-20"
                    className={`${resource.dateFont} text-base leading-[19.2px] tracking-[-0.32px] text-[#0061fe]`}
                  >
                    {resource.date}
                  </time>
                  <h3 className="[font-family:'Suisse_Intl_Trial-Regular',Helvetica] mt-3 text-2xl font-normal leading-[28.8px] tracking-[-0.32px] text-[#f0f0f0]">
                    {resource.title}
                  </h3>
                  <p className="[font-family:'Suisse_Intl_Trial-Regular',Helvetica] mt-[7px] text-base font-normal leading-[19.2px] text-[#585d70]">
                    {resource.description}
                  </p>
                </div>
              </CardContent>
            </Card>
          </article>
        ))}
      </div>
    </section>
  );
};
